<template>
<canvas class='hand-cursor' :ref='uniqueCanvasId + renderingKey' :width='canvasWidth' :height='canvasHeight' @click='canvasClicked'/>
</template>

<script>

export default {
    // https://stackoverflow.com/questions/40410332/vuejs-access-child-components-data-from-parent
    // https://stackoverflow.com/questions/45478035/start-range-in-v-for-n-in-10-from-zero
    props: {
        rowsCount: {
            type: Number,
            required: true
        },
        columnsCount: {
            type: Number,
            required: true
        },
        cellPixelSize: {
            type: Number,
            required: true
        },
        borderThicknessPixels: {
            type: Number,
            required: true
        },
        borderColor: {
            type: String,
            required: true
        },
        cellSelectedColor: {
            type: String,
            required: true
        },
        cellNotSelectedColor: {
            type: String,
            required: true
        },
        rowMajorArray: { // threading, tieup, treadling
            type: Array,
            required: false
        },
        singleColumnSelection: {
            type: Boolean,
            required: true
        },
        singleRowSelection: {
            type: Boolean,
            required: true
        },
        uniqueCanvasId: {
            type: String,
            required: true
        },
        isReadOnly: {
            type: Boolean,
            required: true
        },
        showInverse: {
            type: Boolean,
            required: true
        },
        renderingKey: {
            type: String,
            required: true
        }
    },
    mixins: [],
    components: {

    },
    data() {
        return {
            dataGrid: [],
            vueCanvas: null,
            minimumCellPaddingPixelSize: 4
        }
    },
    methods: {
        renderGrid() {
            this.vueCanvas.fillStyle = 'rgb(255, 255, 255)'
            this.vueCanvas.fillRect(0, 0, this.canvasWidth, this.canvasHeight)

            this.vueCanvas.fillStyle = this.borderColor
            for (let rowIndex=0; rowIndex <= this.rowsCount; rowIndex++) {
                this.vueCanvas.fillRect(0, this.pixelYFromRowNum(rowIndex), this.canvasWidth, this.borderThicknessPixels)
            }

            for (let colIndex=0; colIndex <= this.columnsCount; colIndex++) {
                this.vueCanvas.fillRect(this.pixelXFromColNum(colIndex), 0, this.borderThicknessPixels, this.canvasHeight)
            }

            for (let rowIndex=0; rowIndex < this.rowsCount; rowIndex++) {
                let cellY = this.pixelYFromRowNum(rowIndex) + this.borderThicknessPixels
                for (let colIndex=0; colIndex < this.columnsCount; colIndex++) {
                    let cellX = this.pixelXFromColNum(colIndex) + this.borderThicknessPixels

                    let value = this.dataGrid[rowIndex][colIndex]
                    let color = null
                    if (!this.showInverse) {
                        color = (value ? this.cellSelectedColor : this.cellNotSelectedColor)
                    }
                    else {
                        color = (!value ? this.cellSelectedColor : this.cellNotSelectedColor)
                    }

                    let cellWidth = this.cellPixelSize
                    let cellHeight = this.cellPixelSize

                    let horizOffsetAmount = 0
                    let vertOffsetAmount = 0

                    if (this.cellPixelSize > this.minimumCellPaddingPixelSize) { // put a little space around the black cell centers for good looks
                        horizOffsetAmount = 1
                        vertOffsetAmount = 1
                    }

                    this.vueCanvas.fillStyle=color
                    this.vueCanvas.fillRect(cellX + horizOffsetAmount, cellY + vertOffsetAmount, cellWidth - (horizOffsetAmount * 2), cellHeight - (vertOffsetAmount * 2))
                }
            }
        },
        canvasClicked(e) {
            if (!this.isReadOnly) {
                let canvasElement = this.$refs[this.uniqueCanvasId + this.renderingKey]
                let canvasCurrentRect = canvasElement.getBoundingClientRect()
                let canvasX = Math.trunc(e.clientX - canvasCurrentRect.left)
                let canvasY = Math.trunc(e.clientY - canvasCurrentRect.top)
                let rowNum = this.rowNumFromPixelY(canvasY)
                let colNum = this.colNumFromPixelX(canvasX)

                if (rowNum >= this.rowsCount) { // can happen if last dividing line is clicked after final cell
                    rowNum = this.rowsCount-1
                }

                if (colNum >= this.colsCount) {  // can happen if last dividing line is clicked after final cell
                    colNum = this.rolsCount-1
                }

                this.toggleCell(rowNum, colNum)
            }
        },
        toggleCell(rowIndex, colIndex) {
            if (this.singleColumnSelection) {
                this.clearEntireRow(rowIndex)
            }
            if (this.singleRowSelection) {
                this.clearEntireColumn(colIndex)
            }
            let newValue = !(this.dataGrid[rowIndex][colIndex])
            // console.log('in toggleCell, rowIndex: ' + rowIndex + ', colIndex: ' + colIndex + ' newValue: ' + newValue)
            // console.log(this.dataGrid[rowIndex][colIndex])
            this.dataGrid[rowIndex][colIndex] = newValue
            // reactivity didn't work with nested v-for loops even with Vue.set for array assignments, just do this instead
            // this.$forceUpdate()
            this.renderGrid()
            this.$emit('gridDataChanged', { 'rowIndex': rowIndex, 'colIndex': colIndex }) // lets the parent know so other draft components like the drawdown can be redrawn
        },
        pixelYFromRowNum(rowNum) {
            return rowNum * this.totalCellHeight
        },
        pixelXFromColNum(colNum) {
            return colNum * this.totalCellWidth
        },
        rowNumFromPixelY(y) {
            return Math.trunc(y / this.totalCellHeight)
        },
        colNumFromPixelX(x) {
            return Math.trunc(x / this.totalCellWidth)
        },
        clearEntireColumn(colNum) {
            for (let rowNum=0; rowNum < this.rowsCount; rowNum++) {
                this.dataGrid[rowNum][colNum] = false
            }
        },
        clearEntireRow(rowNum) {
            for (let colNum=0; colNum < this.columnsCount; colNum++) {
                this.dataGrid[rowNum][colNum] = false
            }
        }
    },

    watch: { // watchers are called on changes to data
        showInverse: function(newShowInverse, oldShowInverse) {
            this.renderGrid()
        }
    },
    computed: {
        canvasWidth: function() {
            return (this.columnsCount * this.cellPixelSize) + ((this.columnsCount+1) * (this.borderThicknessPixels))
        },
        canvasHeight: function() {
            return (this.rowsCount * this.cellPixelSize) + ((this.rowsCount+1) * (this.borderThicknessPixels))
        },
        totalCellHeight: function() {
            return this.borderThicknessPixels + this.cellPixelSize
        },
        totalCellWidth: function() {
            return this.borderThicknessPixels + this.cellPixelSize
        }
    },
    created() {
        this.dataGrid = this.rowMajorArray  // reference, changes original, which we want here as parent is notified through emit
    },
    mounted() {
        let c = this.$refs[this.uniqueCanvasId + this.renderingKey]
        let ctx = c.getContext('2d')
        this.vueCanvas = ctx
        this.renderGrid()
    },
    updated() {  // updated is called on changes to the DOM
        this.dataGrid = this.rowMajorArray
        this.renderGrid()
    }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/Grid.vue